import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Phone } from 'lucide-react';
import { stateName } from '#shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, formatPhone } from '../../lib/format';
import { DocLink, LeadLink } from '../../lib/links';
import { DOC_NAME, STATUS_LABEL, inr, type Customer, type DocSummary } from '../../lib/sales';
import { Badge, ButtonLink, Drawer, Skeleton, TableSkeleton, cx } from '../ui';

/** A customer at a glance: contact, the lead they came from, what they owe, every quotation and invoice. */
export function CustomerDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { can } = useAuth();
  const [tab, setTab] = useState<'documents' | 'statement'>('documents');
  const c = useQuery({ queryKey: ['customer', id], queryFn: () => api.get<Customer>(`/customers/${id}`) });
  const docs = useQuery({ queryKey: ['documents', 'customer', id], queryFn: () => api.get<DocSummary[]>(`/documents?customer=${id}`) });
  const ledger = useQuery({
    queryKey: ['ledger', id],
    queryFn: () => api.get<{ date: string; kind: string; ref: string; debit: number; credit: number; balance: number; documentId: string }[]>(`/customers/${id}/ledger`),
    enabled: tab === 'statement',
  });
  const cu = c.data;
  const due = cu ? cu.invoiced - cu.received : 0;

  return (
    <Drawer open wide onClose={onClose} title={cu?.name ?? 'Customer'} description={cu ? [cu.contactPerson, cu.gstin && `GSTIN ${cu.gstin}`, stateName(cu.stateCode)].filter(Boolean).join(' · ') : undefined}
      footer={can('documents.manage') && <ButtonLink to={`/sales/documents/new?type=quotation&customer=${id}`}>New quotation</ButtonLink>}>
      {!cu ? <Skeleton className="h-40" /> : (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-4 text-sm">
            {cu.phone && <a href={`tel:${cu.phone}`} className="inline-flex items-center gap-1.5 hover:underline"><Phone className="size-4 text-slate-400" />{formatPhone(cu.phone)}</a>}
            {cu.email && <a href={`mailto:${cu.email}`} className="inline-flex items-center gap-1.5 hover:underline"><Mail className="size-4 text-slate-400" />{cu.email}</a>}
            {cu.leadId && cu.leadName && <span>From lead <LeadLink id={cu.leadId} number={cu.leadNumber} name={cu.leadName} /></span>}
          </div>
          {cu.billingAddress && <p className="text-sm whitespace-pre-line text-slate-600 dark:text-slate-400">{cu.billingAddress}</p>}
          <div className="grid grid-cols-3 gap-3">
            {([['Billed', cu.invoiced, ''], ['Received', cu.received, 'text-emerald-700'], ['Due', due, due > 0.5 ? 'text-amber-700' : '']] as const).map(([k, v, cls]) => (
              <div key={k} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800"><p className="text-xs text-slate-500">{k}</p><p className={cx('text-lg font-semibold', cls)}>{inr(v, false)}</p></div>
            ))}
          </div>

          <nav className="flex gap-6 border-b border-slate-200 dark:border-slate-800">
            {([['documents', `Quotations & invoices${docs.data ? ` (${docs.data.length})` : ''}`], ['statement', 'Statement']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} className={cx('-mb-px border-b-2 pb-2 text-sm font-medium', tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500')}>{label}</button>
            ))}
          </nav>

          {tab === 'documents' && (!docs.data ? <TableSkeleton rows={3} /> : docs.data.length === 0 ? <p className="text-sm text-slate-500">No quotations or invoices yet.</p> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 uppercase"><tr><th className="w-10 py-2">#</th><th>Document</th><th>Date</th><th>Status</th><th className="text-right">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {docs.data.map((d, i) => (
                  <tr key={d.id}>
                    <td className="py-2 text-slate-500">{i + 1}</td>
                    <td><DocLink id={d.id}>{DOC_NAME[d.docType]} {d.number ?? '(draft)'}</DocLink></td>
                    <td>{formatDate(d.issueDate)}</td>
                    <td><Badge tone={STATUS_LABEL[d.status].tone}>{STATUS_LABEL[d.status].label}</Badge></td>
                    <td className="text-right font-medium">{inr(d.grandTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}

          {tab === 'statement' && (!ledger.data ? <TableSkeleton rows={3} /> : ledger.data.length === 0 ? <p className="text-sm text-slate-500">No invoices yet.</p> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 uppercase"><tr><th className="w-10 py-2">#</th><th>Date</th><th>Details</th><th className="text-right">Billed</th><th className="text-right">Paid</th><th className="text-right">Balance</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {ledger.data.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 text-slate-500">{i + 1}</td>
                    <td>{formatDate(r.date)}</td>
                    <td><DocLink id={r.documentId}>{r.kind === 'invoice' ? `Invoice ${r.ref}` : `Payment · ${r.ref}`}</DocLink></td>
                    <td className="text-right">{r.debit ? inr(r.debit) : ''}</td><td className="text-right text-emerald-700">{r.credit ? inr(r.credit) : ''}</td>
                    <td className="text-right font-medium">{inr(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      )}
    </Drawer>
  );
}
